import { useTranslation } from '@/i18n';
import { useGlobalContainerRef } from '@/wc/helper/ref';
import {
  ButtonV2,
  IconInfo,
  Tooltip,
} from '@moneyforward/ap-frontend-components';
import classNames from 'classnames/bind';
import { type FC, type ReactNode, memo } from 'react';
import styles from './styles.module.scss';

const cx = classNames.bind(styles);

type TitleInfo = {
  title: string;
  number: string;
};

export type DrawerTitleProps = {
  titleInfo: TitleInfo;
  hasApprovalRoute?: boolean;
  onOpenApprovalRoute?: () => void;
  /** NA-3164: optional trailing region (e.g. Detail / Edit), admin-specific layout. */
  headerActions?: ReactNode;
};

export const DrawerTitle: FC<DrawerTitleProps> = memo(
  ({
    titleInfo,
    hasApprovalRoute = false,
    onOpenApprovalRoute,
    headerActions,
  }) => {
    const { t } = useTranslation();
    const container = useGlobalContainerRef();

    const approvalRouteButton = (
      <ButtonV2
        onClick={onOpenApprovalRoute}
        isTransparent
        isDisabled={!hasApprovalRoute}
        leftIcon={<IconInfo size={0} />}
      >
        {t('approving_invoice_report_detail_approval_route')}
      </ButtonV2>
    );

    return (
      <div className={cx('container')}>
        <div className={cx('header-leading-container')}>
          <div className={cx('info-container')}>
            <div className={cx('payment-request-number')}>
              <span className={cx('label')}>
                {t('approving_invoice_report_detail_number')}：
              </span>
              <span className={cx('value')}>{titleInfo.number}</span>
            </div>
            <div className={cx('payment-request-title')}>
              <span className={cx('label')}>
                {t('approving_invoice_report_detail_title')}：
              </span>
              <span className={cx('value')}>{titleInfo.title}</span>
              <div className={cx('approval-route-button')}>
                {hasApprovalRoute ? (
                  approvalRouteButton
                ) : (
                  <Tooltip
                    title={t('approval_route.button.disabled_tooltip')}
                    getPopupContainer={container}
                  >
                    <span>{approvalRouteButton}</span>
                  </Tooltip>
                )}
              </div>
            </div>
          </div>
        </div>
        {headerActions ? (
          <div className={cx('header-trailing')}>{headerActions}</div>
        ) : null}
      </div>
    );
  }
);

DrawerTitle.displayName = 'AdminInvoiceReportDrawerTitle';
